import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { WorkoutStepData } from '@me-fit-mono/typings';
import { WorkoutComponent } from 'src/app/modules/workout-viewer/workout.component';
import { AlgoliaService } from 'src/app/services/algolia.service';
import { PendingOnboardingWorkoutService } from 'src/app/services/pending-onboarding-workout.service';
import { OnboardingStateService } from 'src/app/state/onboarding.state.service';
import {map} from 'rxjs/operators';
import { IonModal, ModalController } from '@ionic/angular';
import { WorkoutPrintService } from 'src/app/services/workout-print.service';
import { ExerciseRendererService } from 'src/app/modules/workout-viewer/exercise-renderer.service';

@Component({
  selector: 'me-onboarding-workout-builder-page',
  templateUrl: './onboarding-workout-builder.page.html',
  styleUrls: ['./onboarding-workout-builder.page.scss'],
})
export class OnboardingWorkoutBuilderPage implements OnInit {

  @ViewChild('completionModal') completionModal: IonModal;

  private workoutComponent: WorkoutComponent | undefined;
  private workoutSteps: WorkoutStepData[] = [];

  @ViewChild(WorkoutComponent)
  set workoutEditor(component: WorkoutComponent | undefined) {
    this.workoutComponent = component;

    if (component) {
      component.setValue(this.workoutSteps);
    }
  }

  workoutForm = this.formBuilder.group({
    name: [null, Validators.required],
    description: [null],
  });

  ready$ = this.$onboarding.workoutBuilderBootstrap.state$.pipe(
    map(state => !state.error && !state.isLoading)
  );

  loading$ = this.$onboarding.workoutBuilderBootstrap.isLoading$();


  error$ = this.$onboarding.workoutBuilderBootstrap.getError$();

  currentYear = new Date().getFullYear();

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly $onboarding: OnboardingStateService,
    private readonly $algolia: AlgoliaService,
    private readonly $pendingWorkout: PendingOnboardingWorkoutService,
    private readonly router: Router,
    private modalCtrl: ModalController,
    private readonly $workoutPrint: WorkoutPrintService,
    private readonly $exerciseRenderer: ExerciseRendererService
  ) {}

  async ngOnInit() {
    try {
      const bootstrap = await this.$onboarding.workoutBuilderBootstrap.call();

      this.$algolia.initAnonymous({
        apiKey: bootstrap.algoliaConfig.apiKey,
        defaultIndexName: bootstrap.algoliaConfig.indexName,
      });
      this.workoutForm.patchValue({
        name: bootstrap.workout.name,
        description: bootstrap.workout.description,
      });
      this.workoutSteps = bootstrap.workout.steps;
    } catch (error) {
      console.error('[OnboardingWorkoutBuilderPage] failed to load workout builder', error);
    }
  }

  async openCompletionModal() {
    if (this.workoutForm.invalid) {
      this.workoutForm.markAllAsTouched();
      return;
    }

    await this.completionModal?.present();
  }

  async continueToOnboarding() {
    this.$pendingWorkout.save({
      name: this.workoutForm.controls.name.value ?? '',
      description: this.workoutForm.controls.description.value ?? '',
      steps: this.workoutComponent?.getValue() ?? [],
    });
    await this.dismissCompletionModal();
    this.router.navigate(['/onboarding']);
  }

  async printWorkout() {
    const steps = this.workoutComponent?.getValue() ?? [];
    const exerciseNames = Object.fromEntries(
      await Promise.all(
        steps.map(async step => [
          step.exerciseId,
          await this.$exerciseRenderer.getExerciseName(step.exerciseId),
        ] as [string, string])
      )
    );

    await this.$workoutPrint.print({
      name: this.workoutForm.controls.name.value ?? '',
      description: this.workoutForm.controls.description.value ?? '',
      steps,
      createdInMeFitProUrl: new URL('/onboarding/workout-builder', window.location.origin).href,
      exerciseNames,
    });
  }

  async dismissCompletionModal() {
    await this.completionModal?.dismiss();
  }

}
